import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';
import InlineCallRecorder from '../components/InlineCallRecorder';
import LeadPipelineView from '../components/LeadPipelineView';
import NurtureKpiDashboard from '../components/NurtureKpiDashboard';
import NurtureTableView from '../components/NurtureTableView';
import LeadDetailPanel from '../components/LeadDetailPanel';
import TriageHome from '../components/TriageHome';
import LeadDetailSheet from '../components/LeadDetailSheet';
import QuickLogCallDialog from '../components/QuickLogCallDialog';
import QuickLogFab from '../components/QuickLogFab';
import {
  PRIORITY_CONFIG, STATUS_CONFIG,
  formatRelativeTime, formatDateTime, isOverdue,
} from '../utils/nurtureUtils';
import {
  Users, Phone, Mail, MessageSquare, MessageCircle, Calendar, Clock, Plus,
  AlertTriangle, CheckCircle, ArrowRight, Search, Filter,
  Flame, Thermometer, Snowflake, PhoneCall, Send, FileText,
  History, Target, TrendingUp, RefreshCw, Loader2, X, Download,
  ChevronRight, Edit2, Trash2, Eye, Bell, PlayCircle,
  Copy, Zap, Settings, List,
  BellRing, XCircle, CalendarClock, CheckCircle2, Workflow, Sparkles, ListChecks
} from 'lucide-react';

import LeadActionListView from '../components/LeadActionListView';
import PodPipelineBoard from '../components/nurture/PodPipelineBoard';

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// PRIORITY_CONFIG, STATUS_CONFIG, formatRelativeTime, formatDateTime, isOverdue
// are now exported from /app/frontend/src/utils/nurtureUtils.js

// ==================== IMPORTED COMPONENTS ====================
import StatCard from '../components/nurture/StatCard';
import LeadCard from '../components/nurture/LeadCard';
import FollowUpCard from '../components/nurture/FollowUpCard';
import NewLeadDialog from '../components/nurture/NewLeadDialog';
import NurtureKPIStrip from '../components/nurture/NurtureKPIStrip';
import NurtureLeadsTable from '../components/nurture/NurtureLeadsTable';

// ==================== MAIN COMPONENT ====================

const LeadNurture = ({ embedded = false }) => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const openLead = useCallback((lead) => {
    if (lead && lead.id) navigate(`/lead-nurture/leads/${lead.id}`);
  }, [navigate]);
  const [loading, setLoading] = useState(true);
  const [options, setOptions] = useState({});
  const [dashboard, setDashboard] = useState({});
  const [leads, setLeads] = useState([]);
  const [followUps, setFollowUps] = useState({ today: [], overdue: [], week: [] });
  const [templates, setTemplates] = useState([]);
  const [sequences, setSequences] = useState([]);
  const [dailySummary, setDailySummary] = useState(null);
  const [accountabilityLog, setAccountabilityLog] = useState([]);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [showNewLeadDialog, setShowNewLeadDialog] = useState(false);
  const [activeView, setActiveView] = useState('board');
  const [followupsView, setFollowupsView] = useState(null);
  const [openSheetLead, setOpenSheetLead] = useState(null);
  const [quickLogOpen, setQuickLogOpen] = useState(false);

  // Global keyboard shortcut: 'C' opens Quick Log Call (unless typing in input).
  useEffect(() => {
    const handler = (e) => {
      if (e.repeat) return;
      const tag = (e.target?.tagName || '').toLowerCase();
      if (['input', 'textarea', 'select'].includes(tag)) return;
      if (e.target?.isContentEditable) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if ((e.key || '').toLowerCase() === 'c' && !quickLogOpen) {
        e.preventDefault();
        setQuickLogOpen(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [quickLogOpen]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  // Helper function for authenticated API calls
  const authFetch = useCallback((url, options = {}) => {
    return fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
  }, [token]);

  const fetchData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      const [optionsRes, dashboardRes, leadsRes, todayRes, overdueRes, weekRes, templatesRes, sequencesRes, summaryRes] = await Promise.all([
        fetch(`${backendUrl}/api/nurture/options`, { headers }).then(r => r.json()),
        fetch(`${backendUrl}/api/nurture/dashboard`, { headers }).then(r => r.json()),
        fetch(`${backendUrl}/api/nurture/leads`, { headers }).then(r => r.json()),
        fetch(`${backendUrl}/api/nurture/follow-ups?due_today=true`, { headers }).then(r => r.json()),
        fetch(`${backendUrl}/api/nurture/follow-ups?overdue=true`, { headers }).then(r => r.json()),
        fetch(`${backendUrl}/api/nurture/follow-ups?this_week=true`, { headers }).then(r => r.json()),
        fetch(`${backendUrl}/api/nurture/templates?is_seed=false`, { headers }).then(r => r.json()),
        fetch(`${backendUrl}/api/nurture/sequences`, { headers }).then(r => r.json()),
        fetch(`${backendUrl}/api/nurture/daily-summary`, { headers }).then(r => r.json())
      ]);

      setOptions(optionsRes);
      setDashboard(dashboardRes);
      setLeads(leadsRes);
      setFollowUps({ today: todayRes, overdue: overdueRes, week: weekRes });
      setTemplates(templatesRes);
      setSequences(sequencesRes);
      setDailySummary(summaryRes);

      // Triage Home + sidebar use the unified today-view shape
      try {
        const todayViewRes = await fetch(`${backendUrl}/api/nurture/follow-ups/today-view`, { headers }).then(r => r.json());
        setFollowupsView(todayViewRes);
      } catch { /* tolerate */ }
    } catch (err) {
      console.error(err);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchAccountabilityLog = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${backendUrl}/api/nurture/accountability-log`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setAccountabilityLog(data);
    } catch (err) {
      console.error('Failed to fetch accountability log:', err);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchData();
      fetchAccountabilityLog();
    }
  }, [fetchData, fetchAccountabilityLog, token]);

  const handleSendSummaryEmail = async () => {
    setSendingEmail(true);
    try {
      const res = await fetch(`${backendUrl}/api/nurture/daily-summary/send-email`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      
      if (data.success) {
        toast.success('Summary email logged! Opening email client...');
        // Open mailto link
        window.open(data.mailto_link, '_blank');
        // Refresh accountability log
        fetchAccountabilityLog();
      } else {
        toast.error('Failed to send summary email');
      }
    } catch (err) {
      toast.error('Failed to send summary email');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleQuickAction = async (lead, action) => {
    try {
      await fetch(`${backendUrl}/api/nurture/leads/${lead.id}/quick-action?action=${action}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success(`${action} logged for ${lead.name}!`);
      fetchData();
    } catch (err) {
      toast.error('Failed to log action');
    }
  };

  const handleCompleteFollowUp = async (followUp, outcome) => {
    try {
      const url = outcome
        ? `${backendUrl}/api/nurture/follow-ups/${followUp.id}/complete?outcome=${encodeURIComponent(outcome)}`
        : `${backendUrl}/api/nurture/follow-ups/${followUp.id}/complete`;
      const resp = await fetch(url, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await resp.json().catch(() => ({}));
      if (data.auto_parked) {
        toast.warning('Lead auto-parked to Long-term Nurture (3 no-contact attempts).');
      } else if (outcome && ['no_answer', 'voicemail', 'no_response'].includes(outcome)) {
        const attempts = data.no_answer_attempts || 0;
        toast.info(`Attempt logged${attempts ? ` — ${attempts}/3` : ''}`);
      } else {
        toast.success('Follow-up completed!');
      }
      fetchData();
    } catch (err) {
      toast.error('Failed to complete follow-up');
    }
  };

  // Drag-drop: move single lead to new status
  const handleMoveLead = async (leadId, newStatus) => {
    // Optimistic update
    setLeads(prev => prev.map(l => l.id === leadId ? { ...l, status: newStatus } : l));
    try {
      const res = await fetch(`${backendUrl}/api/nurture/leads/${leadId}`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      if (!res.ok) throw new Error('Move failed');
      toast.success('Lead moved');
      fetchData();
    } catch (err) {
      toast.error('Failed to move lead');
      fetchData();
    }
  };

  // Bulk status update
  const handleBulkUpdateStatus = async (leadIds, newStatus) => {
    try {
      const res = await fetch(`${backendUrl}/api/nurture/leads/bulk`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ lead_ids: leadIds, status: newStatus })
      });
      if (!res.ok) throw new Error('Bulk update failed');
      const data = await res.json();
      toast.success(`Moved ${data.updated} lead(s)`);
      fetchData();
    } catch (err) {
      toast.error('Failed to bulk update');
    }
  };

  // Bulk delete
  const handleBulkDelete = async (leadIds) => {
    try {
      const res = await fetch(`${backendUrl}/api/nurture/leads/bulk-delete`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ lead_ids: leadIds })
      });
      if (!res.ok) throw new Error('Bulk delete failed');
      const data = await res.json();
      toast.success(`Deleted ${data.deleted} lead(s)`);
      fetchData();
    } catch (err) {
      toast.error('Failed to bulk delete');
    }
  };

  // Inline update (called from table view dropdowns)
  const handleInlineUpdate = async (leadId, fields) => {
    setLeads(prev => prev.map(l => l.id === leadId ? { ...l, ...fields } : l));
    try {
      const res = await fetch(`${backendUrl}/api/nurture/leads/${leadId}`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(fields)
      });
      if (!res.ok) throw new Error('Update failed');
      toast.success('Updated');
      fetchData();
    } catch (err) {
      toast.error('Failed to update');
      fetchData();
    }
  };

  // Filter leads
  const filteredLeads = leads.filter(lead => {
    if (searchTerm && !lead.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !lead.email?.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !lead.phone?.includes(searchTerm)) {
      return false;
    }
    if (filterPriority !== 'all' && lead.priority !== filterPriority) return false;
    if (filterStatus !== 'all' && lead.status !== filterStatus) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-page">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className={embedded ? 'flex flex-col text-slate-900 dark:text-slate-100' : 'min-h-screen flex flex-col bg-page text-slate-900 dark:text-slate-100 transition-colors'}>
      {!embedded && <Header />}
      <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 py-6 sm:py-10 space-y-6 sm:space-y-8" data-testid="lead-nurture-page">

        {/* View switcher — horizontal pill tabs (mobile + desktop) */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide flex-1 min-w-0 pb-1">
            {[
              { k: 'board',     l: 'Pipeline',   icon: Workflow },
              { k: 'leads',     l: 'All Leads',  icon: Users },
              { k: 'triage',    l: 'Triage',     icon: Sparkles },
              { k: 'analytics', l: 'Analytics',  icon: TrendingUp },
            ].map(({ k, l, icon: Icon }) => (
              <button
                key={k}
                onClick={() => setActiveView(k)}
                className={cn(
                  'inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-medium transition-all flex-shrink-0',
                  activeView === k
                    ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900 shadow-md'
                    : 'bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 hover:border-slate-400 dark:hover:border-slate-600',
                )}
                data-testid={`nurture-tab-${k}`}
              >
                <Icon className="w-3.5 h-3.5" /> {l}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button
              variant="outline" size="sm" onClick={fetchData}
              className="dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-700"
              title="Refresh"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button
              variant="outline" size="sm"
              onClick={() => {
                const url = `${process.env.REACT_APP_BACKEND_URL || ''}/api/nurture/leads/export.csv`;
                fetch(url, { headers: { Authorization: `Bearer ${token}` } })
                  .then((r) => r.ok ? r.blob() : Promise.reject(new Error('Export failed')))
                  .then((blob) => {
                    const a = document.createElement('a');
                    a.href = URL.createObjectURL(blob);
                    a.download = `nurture-leads-${new Date().toISOString().slice(0, 10)}.csv`;
                    a.click();
                    URL.revokeObjectURL(a.href);
                  })
                  .catch(() => toast.error('Could not export'));
              }}
              className="dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-700"
              data-testid="export-csv-btn"
              title="Export leads as CSV (hand-off to main CRM)"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline ml-1">Export</span>
            </Button>
            <Button
              onClick={() => setShowNewLeadDialog(true)}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              size="sm"
              data-testid="add-lead-btn"
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline ml-1">Add Lead</span>
            </Button>
          </div>
        </div>

        {/* Tab content */}
        {activeView === 'board' && (
          <PodPipelineBoard token={token} onOpenLead={(l) => setOpenSheetLead(l)} />
        )}

        {activeView === 'triage' && (
          <TriageHome
            leads={leads}
            followups={followupsView}
            loading={loading}
            onOpenLead={(l) => setOpenSheetLead(l)}
            onLogAction={(method, l) => {
              setOpenSheetLead(l);
              toast.info(`${method} ready for ${l.name}`);
            }}
            onAddLead={() => setShowNewLeadDialog(true)}
          />
        )}

        {activeView === 'action' && (
          <div className="space-y-4">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-100 dark:bg-cyan-500/10 border border-cyan-300 dark:border-cyan-500/30 text-cyan-700 dark:text-cyan-300 text-xs font-medium mb-2">
                <ListChecks className="h-3 w-3" /> Smart Inbox
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">Action List</h2>
              <p className="text-slate-600 dark:text-slate-400 mt-1">
                Single ranked list of what needs your attention next — overdue first, hot leads next, no scrolling, no columns.
              </p>
            </div>
            <LeadActionListView
              leads={leads}
              followUps={followUps}
              loading={loading}
              onSelectLead={(l) => setOpenSheetLead(l)}
              onCompleteFollowUp={handleCompleteFollowUp}
            />
          </div>
        )}

        {activeView === 'pipeline' && (
          <div className="space-y-4">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100 dark:bg-emerald-500/10 border border-emerald-300 dark:border-emerald-500/30 text-emerald-700 dark:text-emerald-300 text-xs font-medium mb-2">
                <Workflow className="h-3 w-3" /> Stage Kanban
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">Drag-and-drop pipeline</h2>
              <p className="text-slate-600 dark:text-slate-400 mt-1">Move leads through stages — auto-schedules follow-ups on each transition.</p>
            </div>
            <LeadPipelineView
              leads={leads}
              onSelectLead={(l) => setOpenSheetLead(l)}
              onMoveLead={handleMoveLead}
              onBulkUpdateStatus={handleBulkUpdateStatus}
              onBulkDelete={handleBulkDelete}
            />
          </div>
        )}

        {activeView === 'analytics' && (
          <div className="space-y-4">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100 dark:bg-emerald-500/10 border border-emerald-300 dark:border-emerald-500/30 text-emerald-700 dark:text-emerald-300 text-xs font-medium mb-2">
                <TrendingUp className="h-3 w-3" /> Conversion & KPIs
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">Analytics</h2>
            </div>
            <NurtureKpiDashboard token={token} />
          </div>
        )}

        {activeView === 'dashboard-LEGACY-HIDDEN' && (
          <div className="space-y-6">
            {/* CRM-style KPI dashboard */}
            <NurtureKpiDashboard token={token} />

            {/* Operational tiles (existing) */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard
                title="URGENT - Not Contacted 24h+"
                value={dashboard.urgent_leads || 0}
                icon={AlertTriangle}
                color="bg-red-500"
                urgent={true}
              />
              <StatCard
                title="Overdue Follow-Ups"
                value={dashboard.follow_ups_overdue || 0}
                icon={Clock}
                color="bg-orange-500"
                urgent={dashboard.follow_ups_overdue > 0}
              />
              <StatCard
                title="Due Today"
                value={dashboard.follow_ups_due_today || 0}
                icon={Calendar}
                color="bg-blue-500"
              />
              <StatCard
                title="This Week"
                value={dashboard.follow_ups_this_week || 0}
                icon={Target}
                color="bg-emerald-500"
              />
            </div>

            {/* Secondary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard
                title="New - Not Contacted"
                value={dashboard.not_contacted_yet || 0}
                icon={Users}
                color="bg-purple-500"
              />
              <StatCard
                title="Recently Contacted"
                value={dashboard.recently_contacted || 0}
                icon={CheckCircle}
                color="bg-teal-500"
              />
              <StatCard
                title="No Follow-Up Set"
                value={dashboard.leads_no_follow_up || 0}
                icon={Bell}
                color="bg-amber-500"
                urgent={dashboard.leads_no_follow_up > 0}
              />
              <StatCard
                title="Total Active Leads"
                value={dashboard.total_active_leads || 0}
                icon={TrendingUp}
                color="bg-slate-500"
              />
            </div>

            {/* Overdue Follow-Ups */}
            {followUps.overdue.length > 0 && (
              <Card className="border-red-200 dark:border-rose-500/30 bg-red-50 dark:bg-rose-950/30">
                <CardHeader>
                  <CardTitle className="text-red-700 dark:text-rose-300 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    Overdue Follow-Ups ({followUps.overdue.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {followUps.overdue.slice(0, 5).map(fu => (
                    <FollowUpCard
                      key={fu.id}
                      followUp={fu}
                      onComplete={handleCompleteFollowUp}
                      onSelectLead={(partial) => {
                        const full = leads.find(l => l.id === (partial?.id || fu.lead_id));
                        openLead(full || partial || null);
                      }}
                    />
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Due Today */}
            {followUps.today.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-blue-600" />
                    Due Today ({followUps.today.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {followUps.today.map(fu => (
                    <FollowUpCard
                      key={fu.id}
                      followUp={fu}
                      onComplete={handleCompleteFollowUp}
                      onSelectLead={(partial) => {
                        const full = leads.find(l => l.id === (partial?.id || fu.lead_id));
                        openLead(full || partial || null);
                      }}
                    />
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Recent Leads */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-slate-600" />
                    Recent Leads
                  </span>
                  <Button variant="ghost" size="sm" onClick={() => setActiveView('leads')}>
                    View All <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {leads.slice(0, 5).map(lead => (
                  <LeadCard
                    key={lead.id}
                    lead={lead}
                    onSelect={openLead}
                    onQuickAction={handleQuickAction}
                  />
                ))}
              </CardContent>
            </Card>
          </div>
        )}

        {activeView === 'leads' && (
          <div className="space-y-5" data-testid="lead-nurture-leads-view">
            {/* Page hero */}
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-crm-blue/10 dark:bg-crm-blue/15 border border-crm-blue/20 text-crm-blue dark:text-blue-300 text-xs font-semibold mb-2">
                  <Users className="h-3 w-3" /> Pre-Qualification CRM
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
                  Lead Nurture
                  <span className="text-base font-medium text-slate-400 dark:text-slate-500 tabular-nums">{leads.length}</span>
                </h2>
                <p className="text-slate-600 dark:text-slate-400 text-sm mt-1">
                  Nurture leads from first enquiry until they're ready to apply, then graduate them to your bank CRM.
                </p>
              </div>
            </div>

            {/* KPI strip */}
            <NurtureKPIStrip token={token} />

            {/* Redesigned table with built-in filters + bulk action bar */}
            <NurtureLeadsTable
              leads={leads}
              loading={loading}
              currentUserId={user?.id}
              onOpenLead={(l) => setOpenSheetLead(l)}
              onAddLead={() => setShowNewLeadDialog(true)}
              onExport={() => {
                const url = `${process.env.REACT_APP_BACKEND_URL || ''}/api/nurture/leads/export.csv`;
                fetch(url, { headers: { Authorization: `Bearer ${token}` } })
                  .then((r) => r.ok ? r.blob() : Promise.reject(new Error('Export failed')))
                  .then((blob) => {
                    const a = document.createElement('a');
                    a.href = URL.createObjectURL(blob);
                    a.download = `nurture-leads-${new Date().toISOString().slice(0, 10)}.csv`;
                    a.click();
                    URL.revokeObjectURL(a.href);
                  })
                  .catch(() => toast.error('Could not export'));
              }}
              onBulkUpdateStatus={handleBulkUpdateStatus}
              onBulkDelete={handleBulkDelete}
              onUpdateLead={handleInlineUpdate}
            />
          </div>
        )}

        {activeView === 'leads-LEGACY-HIDDEN' && (
          <NurtureTableView
            leads={leads}
            onSelectLead={openLead}
            onUpdateLead={handleInlineUpdate}
            onBulkUpdateStatus={handleBulkUpdateStatus}
            onBulkDelete={handleBulkDelete}
          />
        )}

        {activeView === 'followups' && (
          <div className="space-y-6">
            {/* Overdue */}
            <Card className={followUps.overdue.length > 0 ? "border-red-200 bg-red-50" : ""}>
              <CardHeader>
                <CardTitle className={cn("flex items-center gap-2", followUps.overdue.length > 0 && "text-red-700")}>
                  <AlertTriangle className="w-5 h-5" />
                  Overdue ({followUps.overdue.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {followUps.overdue.length === 0 ? (
                  <p className="text-slate-500 text-center py-4">No overdue follow-ups! 🎉</p>
                ) : (
                  followUps.overdue.map(fu => (
                    <FollowUpCard
                      key={fu.id}
                      followUp={fu}
                      onComplete={handleCompleteFollowUp}
                      onSelectLead={(partial) => {
                        const full = leads.find(l => l.id === (partial?.id || fu.lead_id));
                        openLead(full || partial || null);
                      }}
                    />
                  ))
                )}
              </CardContent>
            </Card>

            {/* Due Today */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-blue-600" />
                  Due Today ({followUps.today.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {followUps.today.length === 0 ? (
                  <p className="text-slate-500 text-center py-4">No follow-ups due today</p>
                ) : (
                  followUps.today.map(fu => (
                    <FollowUpCard
                      key={fu.id}
                      followUp={fu}
                      onComplete={handleCompleteFollowUp}
                      onSelectLead={(partial) => {
                        const full = leads.find(l => l.id === (partial?.id || fu.lead_id));
                        openLead(full || partial || null);
                      }}
                    />
                  ))
                )}
              </CardContent>
            </Card>

            {/* This Week */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-emerald-600" />
                  This Week ({followUps.week.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {followUps.week.length === 0 ? (
                  <p className="text-slate-500 text-center py-4">No follow-ups scheduled this week</p>
                ) : (
                  followUps.week.map(fu => (
                    <FollowUpCard
                      key={fu.id}
                      followUp={fu}
                      onComplete={handleCompleteFollowUp}
                      onSelectLead={(partial) => {
                        const full = leads.find(l => l.id === (partial?.id || fu.lead_id));
                        openLead(full || partial || null);
                      }}
                    />
                  ))
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {activeView === 'templates' && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-purple-600" />
                    Message Templates
                  </span>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={fetchData}>
                      <RefreshCw className="w-4 h-4 mr-1" /> Refresh
                    </Button>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-500 mb-4">
                  Use these templates when contacting leads. Variables like {'{First Name}'} and {'{Loan Type}'} will be automatically replaced with the lead's actual data.
                </p>
                
                {/* SMS Templates */}
                <div className="mb-6">
                  <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-blue-600" />
                    SMS Templates ({templates.filter(t => t.template_type === 'sms').length})
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {templates.filter(t => t.template_type === 'sms').map(template => (
                      <Card key={template.id} className="border-blue-100">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-medium">{template.name}</h4>
                            <Badge variant="outline" className="text-blue-600">{template.category}</Badge>
                          </div>
                          <p className="text-sm text-slate-600 whitespace-pre-wrap">{template.body}</p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
                
                {/* Email Templates */}
                <div className="mb-6">
                  <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <Mail className="w-4 h-4 text-purple-600" />
                    Email Templates ({templates.filter(t => t.template_type === 'email').length})
                  </h3>
                  <div className="space-y-4">
                    {templates.filter(t => t.template_type === 'email').map(template => (
                      <Card key={template.id} className="border-purple-100">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-medium">{template.name}</h4>
                            <Badge variant="outline" className="text-purple-600">{template.category}</Badge>
                          </div>
                          {template.subject && (
                            <p className="text-sm font-medium text-slate-700 mb-2">Subject: {template.subject}</p>
                          )}
                          <p className="text-sm text-slate-600 whitespace-pre-wrap max-h-40 overflow-y-auto">{template.body}</p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
                
                {/* Call Scripts */}
                <div>
                  <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <PhoneCall className="w-4 h-4 text-green-600" />
                    Call Scripts ({templates.filter(t => t.template_type === 'call_script').length})
                  </h3>
                  <div className="space-y-4">
                    {templates.filter(t => t.template_type === 'call_script').map(template => (
                      <Card key={template.id} className="border-green-100">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-medium">{template.name}</h4>
                            <Badge variant="outline" className="text-green-600">{template.category}</Badge>
                          </div>
                          <pre className="text-sm text-slate-600 whitespace-pre-wrap font-sans">{template.body}</pre>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {activeView === 'sequences' && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Zap className="w-5 h-5 text-amber-600" />
                    Follow-Up Sequences
                  </span>
                  <Button variant="outline" size="sm" onClick={fetchData}>
                    <RefreshCw className="w-4 h-4 mr-1" /> Refresh
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-500 mb-4">
                  Automated follow-up sequences for leads who don't respond. Start a sequence from any lead's detail panel.
                </p>
                
                {sequences.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">No sequences configured yet</p>
                ) : (
                  <div className="space-y-4">
                    {sequences.map(seq => (
                      <Card key={seq.id} className="border-amber-100">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between mb-3">
                            <div>
                              <h4 className="font-semibold text-lg">{seq.name}</h4>
                              <p className="text-sm text-slate-500">{seq.description}</p>
                            </div>
                            <Badge className={seq.is_active ? 'bg-green-500' : 'bg-slate-400'}>
                              {seq.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </div>
                          
                          <div className="flex items-center gap-2 flex-wrap">
                            {seq.steps?.map((step, idx) => (
                              <React.Fragment key={idx}>
                                <div className={cn(
                                  "px-3 py-2 rounded-lg text-sm flex items-center gap-2",
                                  step.method === 'Call' ? 'bg-green-50 text-green-700' :
                                  step.method === 'SMS' ? 'bg-blue-50 text-blue-700' :
                                  'bg-purple-50 text-purple-700'
                                )}>
                                  {step.method === 'Call' ? <PhoneCall className="w-3 h-3" /> :
                                   step.method === 'SMS' ? <MessageSquare className="w-3 h-3" /> :
                                   <Mail className="w-3 h-3" />}
                                  <span>Day {step.day}: {step.method}</span>
                                </div>
                                {idx < seq.steps.length - 1 && (
                                  <ArrowRight className="w-4 h-4 text-slate-300" />
                                )}
                              </React.Fragment>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {activeView === 'summary' && dailySummary && (
          <div className="space-y-4 sm:space-y-6">
            {/* Accountability Alert Banner */}
            {(dailySummary.stats.overdue_count > 0 || dailySummary.stats.urgent_leads_count > 0 || dailySummary.stats.no_followup_count > 0) && (
              <Card className="border-red-400 bg-red-50 border-2">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-red-500 rounded-full">
                      <AlertTriangle className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-red-800">Accountability Alert</h3>
                      <p className="text-sm text-red-700">
                        {dailySummary.stats.overdue_count > 0 && `${dailySummary.stats.overdue_count} overdue follow-ups. `}
                        {dailySummary.stats.urgent_leads_count > 0 && `${dailySummary.stats.urgent_leads_count} leads not contacted in 24h+. `}
                        {dailySummary.stats.no_followup_count > 0 && `${dailySummary.stats.no_followup_count} leads have no follow-up scheduled.`}
                      </p>
                    </div>
                    <Button 
                      variant="destructive" 
                      size="sm"
                      onClick={handleSendSummaryEmail}
                      disabled={sendingEmail}
                    >
                      {sendingEmail ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                      Send Alert Email
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Summary Header */}
            <Card className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white">
              <CardContent className="p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <h2 className="text-xl sm:text-2xl font-bold">Daily Lead Summary</h2>
                    <p className="opacity-90 text-sm sm:text-base">{dailySummary.date}</p>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button 
                      className="bg-white text-emerald-700 hover:bg-emerald-50 border-0"
                      size="sm"
                      onClick={() => {
                        navigator.clipboard.writeText(dailySummary.summary_text);
                        toast.success('Summary copied to clipboard!');
                      }}
                    >
                      <Copy className="w-4 h-4 mr-2" /> Copy Summary
                    </Button>
                    <Button 
                      className="bg-white/20 text-white hover:bg-white/30 border border-white/40"
                      size="sm"
                      onClick={handleSendSummaryEmail}
                      disabled={sendingEmail}
                    >
                      {sendingEmail ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
                      Send Summary
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Stats Overview */}
            <div className="grid grid-cols-3 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-4">
              <Card className={dailySummary.stats.overdue_count > 0 ? "border-red-300 bg-red-50" : ""}>
                <CardContent className="p-2 sm:p-4 text-center">
                  <p className={cn("text-xl sm:text-3xl font-bold", dailySummary.stats.overdue_count > 0 ? "text-red-600" : "text-slate-900")}>
                    {dailySummary.stats.overdue_count}
                  </p>
                  <p className="text-xs sm:text-sm text-slate-500">Overdue</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-2 sm:p-4 text-center">
                  <p className="text-xl sm:text-3xl font-bold text-blue-600">{dailySummary.stats.today_count}</p>
                  <p className="text-xs sm:text-sm text-slate-500">Due Today</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-2 sm:p-4 text-center">
                  <p className="text-xl sm:text-3xl font-bold text-purple-600">{dailySummary.stats.tomorrow_count}</p>
                  <p className="text-xs sm:text-sm text-slate-500">Tomorrow</p>
                </CardContent>
              </Card>
              <Card className={dailySummary.stats.urgent_leads_count > 0 ? "border-orange-300 bg-orange-50" : ""}>
                <CardContent className="p-2 sm:p-4 text-center">
                  <p className={cn("text-xl sm:text-3xl font-bold", dailySummary.stats.urgent_leads_count > 0 ? "text-orange-600" : "text-slate-900")}>
                    {dailySummary.stats.urgent_leads_count}
                  </p>
                  <p className="text-xs sm:text-sm text-slate-500">Urgent (24h+)</p>
                </CardContent>
              </Card>
              <Card className={dailySummary.stats.no_followup_count > 0 ? "border-amber-300 bg-amber-50" : ""}>
                <CardContent className="p-2 sm:p-4 text-center">
                  <p className={cn("text-xl sm:text-3xl font-bold", dailySummary.stats.no_followup_count > 0 ? "text-amber-600" : "text-slate-900")}>
                    {dailySummary.stats.no_followup_count}
                  </p>
                  <p className="text-xs sm:text-sm text-slate-500">No Follow-up</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-2 sm:p-4 text-center">
                  <p className="text-xl sm:text-3xl font-bold text-rose-600">{dailySummary.stats.hot_leads_count}</p>
                  <p className="text-xs sm:text-sm text-slate-500">Hot Leads</p>
                </CardContent>
              </Card>
            </div>

            {/* Overdue Section */}
            {dailySummary.overdue_followups.length > 0 && (
              <Card className="border-red-200 bg-red-50">
                <CardHeader>
                  <CardTitle className="text-red-700 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    Overdue Follow-Ups ({dailySummary.overdue_followups.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {dailySummary.overdue_followups.map((fu, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-white rounded-lg">
                        <div className="flex items-center gap-3">
                          <Badge className={fu.method === 'Call' ? 'bg-green-500' : fu.method === 'SMS' ? 'bg-blue-500' : 'bg-purple-500'}>
                            {fu.method}
                          </Badge>
                          <div>
                            <p className="font-medium">{fu.lead?.name || 'Unknown'}</p>
                            <p className="text-sm text-slate-500">{fu.lead?.phone} • {fu.lead?.lead_type}</p>
                          </div>
                        </div>
                        <Badge variant="outline" className="text-red-600">{fu.lead?.priority}</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Today's Schedule */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-blue-600" />
                  Today's Schedule ({dailySummary.today_followups.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {dailySummary.today_followups.length === 0 ? (
                  <p className="text-slate-500 text-center py-4">No follow-ups scheduled for today</p>
                ) : (
                  <div className="space-y-2">
                    {dailySummary.today_followups.map((fu, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="text-sm font-medium text-slate-600 w-16">
                            {new Date(fu.scheduled_at).toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true })}
                          </div>
                          <Badge className={fu.method === 'Call' ? 'bg-green-500' : fu.method === 'SMS' ? 'bg-blue-500' : 'bg-purple-500'}>
                            {fu.method}
                          </Badge>
                          <div>
                            <p className="font-medium">{fu.lead?.name || 'Unknown'}</p>
                            <p className="text-sm text-slate-500">{fu.lead?.phone}</p>
                          </div>
                        </div>
                        <Badge variant="outline">{fu.lead?.priority}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Tomorrow Preview */}
            {dailySummary.tomorrow_followups.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-purple-600" />
                    Tomorrow's Preview ({dailySummary.tomorrow_followups.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {dailySummary.tomorrow_followups.map((fu, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <Badge className={fu.method === 'Call' ? 'bg-green-500' : fu.method === 'SMS' ? 'bg-blue-500' : 'bg-purple-500'}>
                            {fu.method}
                          </Badge>
                          <div>
                            <p className="font-medium">{fu.lead?.name || 'Unknown'}</p>
                            <p className="text-sm text-slate-500">{fu.lead?.phone}</p>
                          </div>
                        </div>
                        <Badge variant="outline">{fu.lead?.priority}</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Leads Needing Attention */}
            {dailySummary.no_followup_leads.length > 0 && (
              <Card className="border-amber-200 bg-amber-50">
                <CardHeader>
                  <CardTitle className="text-amber-700 flex items-center gap-2">
                    <Bell className="w-5 h-5" />
                    Leads Without Follow-Up Scheduled ({dailySummary.no_followup_leads.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {dailySummary.no_followup_leads.map((lead, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-white rounded-lg">
                        <div>
                          <p className="font-medium">{lead.name}</p>
                          <p className="text-sm text-slate-500">{lead.phone} • {lead.lead_type}</p>
                        </div>
                        <Badge variant="outline">{lead.priority}</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Raw Summary Text (for copying) */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-slate-600" />
                    Email-Ready Summary
                  </span>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      navigator.clipboard.writeText(dailySummary.summary_text);
                      toast.success('Copied to clipboard!');
                    }}
                  >
                    <Copy className="w-4 h-4 mr-1" /> Copy All
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <pre className="whitespace-pre-wrap text-sm font-sans bg-slate-50 p-4 rounded-lg max-h-96 overflow-y-auto">
                  {dailySummary.summary_text}
                </pre>
              </CardContent>
            </Card>

            {/* Accountability Log */}
            {accountabilityLog.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-slate-700">
                    <History className="w-5 h-5" />
                    Recent Accountability Actions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {accountabilityLog.slice(0, 5).map((log, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg text-sm">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-emerald-100 rounded-full">
                            <CheckCircle className="w-4 h-4 text-emerald-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-700">{log.description}</p>
                            <p className="text-xs text-slate-500">{formatRelativeTime(log.created_at)}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
        </main>

      {/* New Lead Dialog */}
      <NewLeadDialog
        open={showNewLeadDialog}
        onClose={() => setShowNewLeadDialog(false)}
        options={options}
        onSuccess={fetchData}
        authToken={token}
      />

      {/* Lead Detail slide-over (replaces full-page navigation) */}
      <LeadDetailSheet
        lead={openSheetLead}
        open={!!openSheetLead}
        onClose={() => setOpenSheetLead(null)}
        onUpdate={(updated) => {
          // Refresh data + keep the sheet showing the latest
          fetchData();
          if (updated && updated.id) setOpenSheetLead(updated);
        }}
        options={options}
        templates={templates}
        authToken={token}
      />

      {/* Quick Log Call — keyboard-shortcut + FAB */}
      <QuickLogCallDialog
        open={quickLogOpen}
        onClose={() => setQuickLogOpen(false)}
        leads={leads}
        authToken={token}
        onLogged={() => fetchData()}
      />
      <QuickLogFab
        hidden={quickLogOpen || !!openSheetLead || showNewLeadDialog}
        onClick={() => setQuickLogOpen(true)}
      />
    </div>
  );
};

export default LeadNurture;
